import ks from "../ReUsableTS/kBrowserShared";
import LNG from "../ReUsableTS/kLNG";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableRow } from "./kEditableTableRow";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTablePaging {
    public static AppendFilterInput(Self: kEditableTable, Toolbar: HTMLDivElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Input: HTMLInputElement = document.createElement("input");
            Input.type = "search";
            Input.id = Self.IdPrefix + "-filter";
            Input.className = "kEditableTableFilter";
            let Placeholder: string = Self.FilterPlaceholder;
            if (Placeholder === "") { Placeholder = LNG.SEARCH; }
            Input.placeholder = Placeholder;
            // A placeholder is not an accessible name (it vanishes on typing and some screen readers skip it).
            Input.setAttribute("aria-label", Placeholder);
            Input.addEventListener("input", Self["OnFilterInput"]);
            Input.addEventListener("keydown", Self["OnFilterKeyDown"]);
            Toolbar.appendChild(Input);
            Self.FilterInput = Input;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.AppendFilterInput");
        }
    }

    // Debounced: see FilterDebounceMs. An empty box, or FilterDebounceMs <= 0, filters now.
    public static OnFilterInput(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Input: HTMLInputElement | null = Self.FilterInput;
            if (Input === null) { return; }
            if (Self.FilterDebounceMs <= 0 || Input.value.trim() === "") {
                Self["_ApplyFilterInputNow"]();
                return;
            }
            Self.CancelPendingFilter();
            Self["_FilterDebounceTimer"] = window.setTimeout(Self["_OnFilterDebounceElapsed"], Self.FilterDebounceMs);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.OnFilterInput");
        }
    }

    // Enter flushes a pending debounced filter at once. With nothing pending the grid already
    // shows the box's term, so Enter does nothing - FilterAppliedCB fires once per real change.
    public static OnFilterKeyDown(Self: kEditableTable, KeyEvent: KeyboardEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (KeyEvent.key !== "Enter") { return; }
            if (Self["_FilterDebounceTimer"] === 0) { return; }
            Self["_ApplyFilterInputNow"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.OnFilterKeyDown");
        }
    }

    public static _OnFilterDebounceElapsed(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_FilterDebounceTimer"] = 0;
            Self["_ApplyFilterInputNow"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._OnFilterDebounceElapsed");
        }
    }

    // Reads the box's term into the model and filters, cancelling any pending debounce first.
    public static _ApplyFilterInputNow(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.CancelPendingFilter();
            const Input: HTMLInputElement | null = Self.FilterInput;
            if (Input === null) { return; }
            Self.FilterModel.SetGlobalTerm(Input.value.trim());
            Self.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._ApplyFilterInputNow");
        }
    }

    // Drops a debounced quick-filter that has not run yet (no-op when none is pending).
    public static CancelPendingFilter(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self["_FilterDebounceTimer"] === 0) { return; }
            window.clearTimeout(Self["_FilterDebounceTimer"]);
            Self["_FilterDebounceTimer"] = 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.CancelPendingFilter");
        }
    }

    // True while the quick-filter box holds a term the grid has not applied yet.
    public static HasPendingFilter(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            return Self["_FilterDebounceTimer"] !== 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.HasPendingFilter");
            return false;
        }
    }

    // The single place row visibility is recomputed from FilterModel. Every
    // filter surface (global input, header popup, context menu, advanced
    // dialog, programmatic API) mutates the model then calls this. Rows the
    // user deleted (Hidden) stay hidden regardless of the filter. Returns the
    // number of visible rows so callers can react to an empty result.
    public static ApplyFilters(Self: kEditableTable): number {
        if (ks.KErrorMode === true) { return 0; }
        try {
            const Eligible: kEditableTableRow[] = [];
            let CurrRow: kEditableTableRow;
            for (CurrRow of Self.Rows.Items) {
                if (CurrRow.Hidden === true) { continue; }
                if (Self.FilterModel.RowMatches(CurrRow) === true) {
                    Eligible.push(CurrRow);
                } else {
                    CurrRow.Hide();
                }
            }
            // A single seam decides on-screen rows from the filter-passing set:
            // all of them when pagination is off, else only the current page's
            // slice (and it syncs the pager controls). Row count reflects the
            // whole filtered set, NOT the page size.
            Self["_ShowPageWindow"](Eligible);
            const Visible: number = Eligible.length;
            Self.UpdateRowCount(Visible);
            Self["_UpdateNoMatchRow"](Visible);
            Self["_ApplyGlobalHighlight"]();
            Self.RefreshFilterIndicators();
            if (Self.FilterUI !== null) { Self.FilterUI.Refresh(); }
            if (Self.FilterAppliedCB !== null) { Self.FilterAppliedCB(Visible); }
            return Visible;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.ApplyFilters");
            return 0;
        }
    }

    // Coerces PageSize to a usable positive integer without mutating the public
    // field (a caller may set any number; we defend at read time).
    public static _SafePageSize(Self: kEditableTable): number {
        if (ks.KErrorMode === true) { return 20; }
        try {
            if (Self.PageSize < 1) { return 1; }
            return Math.floor(Self.PageSize);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._SafePageSize");
            return 20;
        }
    }

    // Applies the current page window to the eligible (filter-passing,
    // non-deleted) rows. Pagination OFF => every eligible row is shown (the
    // pre-pagination behaviour, byte-identical for existing callers). Pagination
    // ON => only the PageSize slice for the current page is shown, the rest are
    // hidden, and the pager controls are synced. The page index is clamped into
    // range so a filter or deletion that shrinks the set never strands the view
    // on an empty page.
    public static _ShowPageWindow(Self: kEditableTable, Eligible: kEditableTableRow[]): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.EnablePagination !== true) {
                let PlainRow: kEditableTableRow;
                for (PlainRow of Eligible) { PlainRow.Show(); }
                return;
            }
            const Size: number = Self["_SafePageSize"]();
            const PageCount: number = Math.max(1, Math.ceil(Eligible.length / Size));
            if (Self["_CurrentPage"] > PageCount - 1) { Self["_CurrentPage"] = PageCount - 1; }
            if (Self["_CurrentPage"] < 0) { Self["_CurrentPage"] = 0; }
            const Start: number = Self["_CurrentPage"] * Size;
            const End: number = Start + Size;
            let Index: number = 0;
            let CurrRow: kEditableTableRow;
            for (CurrRow of Eligible) {
                if (Index >= Start && Index < End) { CurrRow.Show(); } else { CurrRow.Hide(); }
                Index = Index + 1;
            }
            Self["_UpdatePaginationControls"](PageCount);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._ShowPageWindow");
        }
    }

    // Number of pages the current filter-passing set spans (>= 1). Returns 1 when
    // pagination is off so callers can treat "one page" uniformly.
    public static GetPageCount(Self: kEditableTable): number {
        if (ks.KErrorMode === true) { return 1; }
        try {
            if (Self.EnablePagination !== true) { return 1; }
            let EligibleCount: number = 0;
            let CurrRow: kEditableTableRow;
            for (CurrRow of Self.Rows.Items) {
                if (CurrRow.Hidden === true) { continue; }
                if (Self.FilterModel.RowMatches(CurrRow) === true) { EligibleCount = EligibleCount + 1; }
            }
            return Math.max(1, Math.ceil(EligibleCount / Self["_SafePageSize"]()));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.GetPageCount");
            return 1;
        }
    }

    // The current zero-based page index (clamped into range on every render).
    public static GetCurrentPage(Self: kEditableTable): number {
        if (ks.KErrorMode === true) { return 0; }
        try {
            return Self["_CurrentPage"];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.GetCurrentPage");
            return 0;
        }
    }

    // Jump to a zero-based page. Out-of-range indices are clamped by the render
    // pass, so callers may pass values that overshoot without guarding.
    public static GoToPage(Self: kEditableTable, PageIndex: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_CurrentPage"] = PageIndex;
            Self.ApplyFilters();
            if (Self.PageChangedCB !== null) { Self.PageChangedCB(Self["_CurrentPage"], Self.GetPageCount()); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.GoToPage");
        }
    }

    public static NextPage(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.GoToPage(Self["_CurrentPage"] + 1);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.NextPage");
        }
    }

    public static PrevPage(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.GoToPage(Self["_CurrentPage"] - 1);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging.PrevPage");
        }
    }

    // Builds the pager bar and paints the first page window once the container is
    // assembled (split out of ToContainer so that method stays within the length
    // limit). Called only when EnablePagination is true. The initial ApplyFilters
    // shows only page 1's slice and syncs the pager labels; with no active filter
    // it is otherwise the same all-rows-eligible pass as before.
    public static _SetupPagination(Self: kEditableTable, Container: HTMLDivElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_BuildPaginationBar"](Container);
            Self.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._SetupPagination");
        }
    }

    // Builds the pager bar (Previous / "Page X of Y" / Next) at the bottom of the
    // container. Called from _SetupPagination only when EnablePagination is true.
    public static _BuildPaginationBar(Self: kEditableTable, Container: HTMLDivElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Bar: HTMLDivElement = document.createElement("div");
            Bar.id = Self.IdPrefix + "-pagination";
            Bar.className = "kEditableTablePagination";
            Bar.setAttribute("role", "navigation");

            const Prev: HTMLButtonElement = document.createElement("button");
            Prev.type = "button";
            Prev.className = "kEditableTablePageBtn kEdtblPagePrev";
            Prev.textContent = LNG.PREVIOUS;
            Prev.addEventListener("click", Self["_OnPrevPageClick"]);

            const Info: HTMLSpanElement = document.createElement("span");
            Info.className = "kEditableTablePageInfo";

            const Next: HTMLButtonElement = document.createElement("button");
            Next.type = "button";
            Next.className = "kEditableTablePageBtn kEdtblPageNext";
            Next.textContent = LNG.NEXT;
            Next.addEventListener("click", Self["_OnNextPageClick"]);

            Bar.appendChild(Prev);
            Bar.appendChild(Info);
            Bar.appendChild(Next);
            Container.appendChild(Bar);

            Self.PaginationDiv = Bar;
            Self.PrevPageButton = Prev;
            Self.NextPageButton = Next;
            Self.PageInfoSpan = Info;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._BuildPaginationBar");
        }
    }

    public static _OnPrevPageClick(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.PrevPage();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._OnPrevPageClick");
        }
    }

    public static _OnNextPageClick(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.NextPage();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._OnNextPageClick");
        }
    }

    // Syncs the "Page X of Y" label and the Prev/Next disabled state. Null-guards
    // every element so it is safe to call before the bar is built (initial render).
    public static _UpdatePaginationControls(Self: kEditableTable, PageCount: number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.PageInfoSpan !== null) {
                const Text: string = LNG.PAGEOF
                    .replace("[X1]", (Self["_CurrentPage"] + 1).toString())
                    .replace("[X2]", PageCount.toString());
                Self.PageInfoSpan.textContent = Text;
            }
            if (Self.PrevPageButton !== null) {
                Self.PrevPageButton.disabled = Self["_CurrentPage"] <= 0;
            }
            if (Self.NextPageButton !== null) {
                Self.NextPageButton.disabled = Self["_CurrentPage"] >= PageCount - 1;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._UpdatePaginationControls");
        }
    }

    // Re-run the filter after an operation that reorders or adds rows, but only
    // when a filter is active — keeps unfiltered grids on their existing fast
    // paths (feature #12).
    public static _ReapplyIfFiltering(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.FilterModel.HasActiveFilters() === false && Self.EnablePagination === false) { return; }
            Self.ApplyFilters();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTablePaging._ReapplyIfFiltering");
        }
    }
}
